import {
  Button,
  Tooltip,
  TreeSelect,
  DatePicker,
  notification,
  Modal,
} from 'antd'
import React, { useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { lambdaAPI } from 'src/utils/interceptor'
import { saveAs } from 'file-saver'
import { VENUES_REPORT_DESCRIPTIONS } from './venuesReportDescriptions'
import ReportGroupSelect from './ReportGroupSelect'
import {
  SAGE50_BLOCKED_TITLE,
  SAGE50_LEFT_OUT_TITLE,
  sage50BoxVenueIds,
  sage50ExportBody,
  sage50ExportFilename,
  sage50FileNote,
  sage50LargeGroupQuestion,
  sage50LeftOutSentence,
  sage50VenuesForFile,
  shouldConfirmSage50Size,
} from './sage50ExportRequest'

const Sage50Export = ({ venues }) => {
  const [selectedVenueIds, setSelectedVenuesIds] = useState([])
  const [leftOutVenues, setLeftOutVenues] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, 'days'),
    moment(),
  ])
  const [errorModalVisible, setErrorModalVisible] = useState(false)
  const [missingVenueCodes, setMissingVenueCodes] = useState([])
  const [leftOutAfterExport, setLeftOutAfterExport] = useState(false)

  const applySageVenues = ids => {
    const file = sage50VenuesForFile(ids, venues)
    setSelectedVenuesIds(sage50BoxVenueIds(ids, venues))
    setLeftOutVenues(file.leftOut)
  }

  const venueOptions = venues?.map(item => ({
    title: item.title,
    value: item._id,
  }))
  const allIds = venueOptions?.map(({ value }) => value)

  const handleDownloadCSV = async () => {
    if (selectedVenueIds.length === 0) {
      notification.warning({
        message: 'No Venues Selected',
        description: 'Please select at least one venue to export.',
      })
      return
    }

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      notification.warning({
        message: 'Date Range Required',
        description: 'Please select a date range for the export.',
      })
      return
    }

    setIsLoading(true)

    try {
      // Format dates as YYYY-MM-DD
      const startDate = dateRange[0].format('YYYY-MM-DD')
      const endDate = dateRange[1].format('YYYY-MM-DD')
      const url = '/reports/sage50'

      const response = await lambdaAPI.post(
        url,
        sage50ExportBody(selectedVenueIds, startDate, endDate),
        { responseType: 'text' },
      )

      const csvContent = response?.data ?? ''

      // Read soft-warning metadata from custom response header (if present)
      const warningsHeader =
        response.headers?.['x-sage50-warnings'] ||
        response.headers?.['X-Sage50-Warnings']
      if (warningsHeader) {
        try {
          const warnings = JSON.parse(warningsHeader)
          const parts = []

          // Show excluded orders warning first (most important)
          if (warnings.excludedOrdersCount) {
            parts.push(
              `${warnings.excludedOrdersCount} order${
                warnings.excludedOrdersCount === 1 ? '' : 's'
              } excluded - ${
                warnings.excludedOrdersReason ||
                'These orders have not been reconciled/saved and are excluded from the export'
              }`,
            )
          }

          // Then show missing field warnings
          if (warnings.missingNominalCodeCount) {
            parts.push(
              `${warnings.missingNominalCodeCount} line${
                warnings.missingNominalCodeCount === 1 ? '' : 's'
              } with no nominal code`,
            )
          }
          if (warnings.missingSageSupplierAccountNumberCount) {
            parts.push(
              `${warnings.missingSageSupplierAccountNumberCount} line${
                warnings.missingSageSupplierAccountNumberCount === 1 ? '' : 's'
              } with no Sage supplier contact number`,
            )
          }
          if (
            warnings.missingInvoiceNumberCount ||
            warnings.missingInvoiceDateCount
          ) {
            const numParts = []
            if (warnings.missingInvoiceNumberCount) {
              numParts.push(
                `${warnings.missingInvoiceNumberCount} without invoice number`,
              )
            }
            if (warnings.missingInvoiceDateCount) {
              numParts.push(
                `${warnings.missingInvoiceDateCount} without invoice date`,
              )
            }
            parts.push(`Some invoice lines ${numParts.join(' and ')}`)
          }

          if (parts.length > 0) {
            notification.warning({
              message: 'Sage 50 export warnings',
              description: `The export completed, but there are potential gaps:\n- ${parts.join(
                '\n- ',
              )}`,
              duration: 10,
            })
          }
          if (warnings.missingVenueCodes?.length) {
            setMissingVenueCodes(warnings.missingVenueCodes)
            setLeftOutAfterExport(true)
            setErrorModalVisible(true)
          }
        } catch (e) {
          // If header isn't valid JSON, ignore
          // eslint-disable-next-line no-console
          console.warn('Failed to parse Sage 50 warnings header', e)
        }
      }

      // Generate filename
      const file = sage50VenuesForFile(selectedVenueIds, venues)
      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      const filename = sage50ExportFilename(file.inFile.length, timestamp)

      // Create blob and download
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      })
      saveAs(blob, filename)

      notification.success({
        message: 'Export Successful',
        description: `Sage 50 export downloaded successfully.`,
        duration: 3,
      })
    } catch (error) {
      console.error('Sage 50 export error:', error)

      // Handle error response
      if (error.response) {
        const status = error.response.status
        let data = error.response.data

        // If responseType is 'text', data might be a JSON string
        if (typeof data === 'string' && data.trim().startsWith('{')) {
          try {
            data = JSON.parse(data)
          } catch (e) {
            // Not JSON, keep as string
          }
        }

        if (status === 400 && data && data.missingVenueCodes) {
          setMissingVenueCodes(data.missingVenueCodes)
          setLeftOutAfterExport(false)
          setErrorModalVisible(true)
          return
        }

        // Try to parse error message
        let errorMessage = 'Failed to generate Sage 50 export.'
        if (typeof data === 'string') {
          errorMessage = data || errorMessage
        } else if (data && data.error) {
          errorMessage = data.error
        } else if (data && data.message) {
          errorMessage = data.message
        }

        notification.error({
          message: 'Export Failed',
          description: errorMessage,
          duration: 5,
        })
      } else {
        notification.error({
          message: 'Export Failed',
          description:
            error.message || 'An unexpected error occurred. Please try again.',
          duration: 5,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const askThenDownload = () => {
    if (!shouldConfirmSage50Size(selectedVenueIds.length)) {
      handleDownloadCSV()
      return
    }
    Modal.confirm({
      title: 'Large Sage 50 export',
      content: sage50LargeGroupQuestion(selectedVenueIds.length),
      okText: 'Generate report',
      cancelText: 'Cancel',
      onOk: () => handleDownloadCSV(),
    })
  }

  return (
    <StyledBlock>
      <HelperText data-testid='report-helper'>
        {VENUES_REPORT_DESCRIPTIONS['Sage 50 Export (CSV)']}
        <br />
        <br />
        Rows will be grouped where the non-monetary fields match (supplier
        account, venue/department code, invoice number &amp; date, PO number,
        nominal code and VAT code). Net and VAT amounts are summed for those
        groups.
        <br />
        <br />
        To get a complete, usable export in Sage 50, please make sure:
        <ul style={{ marginTop: 8, paddingLeft: 18 }}>
          <li>
            <strong>Products</strong>: each venue product has a{' '}
            <strong>Nominal Code</strong> configured in the shopping lists tab.
          </li>
          <li>
            <strong> Your Suppliers Tab</strong>: each supplier has a{' '}
            <strong>Sage Supplier Account Number</strong> configured.
          </li>
          <li>
            <strong>Venues</strong>: each venue has a{' '}
            <strong>Sage Venue Code / Department Code</strong> set in Settings.
          </li>
          <li>
            <strong>Goods receipting</strong>: every order you want to export is{' '}
            <strong>reconciled with an attached invoice</strong>, and that
            invoice has both an <strong>Invoice Number</strong> and an{' '}
            <strong>Invoice Date</strong>.
          </li>
        </ul>
      </HelperText>
      <ReportGroupSelect
        venues={venues}
        onPick={applySageVenues}
        overrideNote={
          selectedVenueIds.length || leftOutVenues.length
            ? sage50FileNote({
                inFile: sage50VenuesForFile(selectedVenueIds, venues).inFile,
                leftOut: leftOutVenues,
              })
            : ''
        }
      />
      <TreeSelect
        treeNodeFilterProp='title'
        allowClear={true}
        placeholder='Select Venues ...'
        treeCheckable={true}
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        dropdownStyle={{ maxHeight: '300px' }}
        style={{ marginBottom: '15px', width: '355px' }}
        onChange={applySageVenues}
        value={selectedVenueIds}
        treeData={[
          {
            title:
              selectedVenueIds.length > 0 ? (
                <span
                  onClick={() => applySageVenues([])}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Unselect all
                </span>
              ) : (
                <span
                  onClick={() => applySageVenues(allIds)}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Select all
                </span>
              ),
            value: 'xxx',
            disableCheckbox: true,
            disabled: true,
          },
          ...venueOptions,
        ]}
      />
      <StyledRangePicker
        value={dateRange}
        onChange={setDateRange}
        format='DD/MM/YYYY'
        style={{ width: '355px', marginBottom: '15px' }}
      />
      {selectedVenueIds.length === 0 ? (
        <Tooltip title='Select at least one venue to generate the Sage 50 export.'>
          <Button
            style={{ width: '355px', marginTop: 20 }}
            className='ant-btn ant-btn-primary'
          >
            Generate Export
          </Button>
        </Tooltip>
      ) : (
        <Button
          loading={isLoading}
          style={{ width: '355px', marginTop: 20 }}
          className='ant-btn ant-btn-primary'
          onClick={askThenDownload}
          disabled={isLoading}
        >
          Generate Export
        </Button>
      )}

      <Modal
        title={
          leftOutAfterExport ? SAGE50_LEFT_OUT_TITLE : SAGE50_BLOCKED_TITLE
        }
        visible={errorModalVisible}
        onOk={() => setErrorModalVisible(false)}
        onCancel={() => setErrorModalVisible(false)}
        okText='OK'
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>
          {leftOutAfterExport
            ? sage50LeftOutSentence(missingVenueCodes)
            : 'The following venues are missing Sage Venue Codes and cannot be exported. Please configure Sage Venue Codes in Settings before exporting:'}
        </p>
        <ul style={{ marginTop: 16, marginBottom: 0 }}>
          {missingVenueCodes.map((venue, index) => (
            <li key={index}>
              <strong>{venue.title}</strong> (ID: {venue.id})
            </li>
          ))}
        </ul>
      </Modal>
    </StyledBlock>
  )
}

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
  }
`

export default Sage50Export
